import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Users, IndianRupee, Loader2 } from "lucide-react";
import { ModuleLinks } from "@/components/ModuleLinks";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ErrorState, StatusPill } from "@/components/Bits";
import { inrShort, num, dmy, BUSINESS_TYPE_LABEL } from "@/lib/format";
import { PhoneInput } from "@/components/PhoneInput";
import { INDIAN_STATES, firstError, splitE164, validateEmail, validateGstin,
  validatePhone, validatePincode, validateRegistrationNo } from "@/lib/validate";

const field = "w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900";
const label = "overline text-slate-400";

const EMPTY = {
  name: "", business_type: "hospital", city: "", state: "", country: "India", pincode: "",
  address: "", phone: "", email: "", gstin: "", registration_no: "", drug_licence: "",
  pharmacy_name: "", admin_name: "", admin_email: "", admin_phone: "",
};

const validateBusiness = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Business name is required.";
  if (!f.city.trim()) e.city = "City is required.";
  if (!f.state.trim()) e.state = "State is required.";
  if (!f.address.trim()) e.address = "Address is required.";
  const p = splitE164(f.phone);
  const pe = validatePhone(p.country, p.national, { required: true, label: "Business phone" });
  if (pe) e.phone = pe;
  const ee = validateEmail(f.email, { required: true, label: "Business email" });
  if (ee) e.email = ee;
  const pin = validatePincode(f.pincode);
  if (pin) e.pincode = pin;
  const needsHospital = f.business_type.includes("hospital");
  const needsPharmacy = f.business_type.includes("pharmacy");
  if (needsHospital) {
    const r = validateRegistrationNo(f.registration_no, "Hospital registration number");
    if (r) e.registration_no = r;
  }
  if (needsPharmacy) {
    const dl = validateRegistrationNo(f.drug_licence, "Drug licence number");
    if (dl) e.drug_licence = dl;
  }
  const g = validateGstin(f.gstin, needsPharmacy);
  if (g) e.gstin = g;
  if (!f.admin_name.trim()) e.admin_name = "Admin name is required.";
  const ae = validateEmail(f.admin_email, { required: true, label: "Admin email" });
  if (ae) e.admin_email = ae;
  const ap = splitE164(f.admin_phone);
  const ape = validatePhone(ap.country, ap.national, { required: true, label: "Admin mobile" });
  if (ape) e.admin_phone = ape;
  return e;
};

const Err = ({ msg }) => msg
  ? <p className="mt-1.5 text-xs font-medium text-red-600">{msg}</p> : null;

export default function Businesses() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(params.get("new") === "1");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");

  const status = params.get("status") || "";
  const type = params.get("type") || "";

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/owner/businesses", {
        params: { status: status || undefined, business_type: type || undefined, q: search || undefined },
      });
      setItems(data.items);
    } catch (e) {
      setError(apiError(e));
    }
  }, [status, type, search]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const v = validateBusiness(form);
    setErrors(v);
    if (Object.keys(v).length) { setFormError(firstError(v)); return; }
    setBusy(true);
    setFormError("");
    try {
      const { data } = await api.post("/owner/businesses", form);
      toast.success(data.message);
      setShowForm(false);
      setForm(EMPTY);
      setErrors({});
      load();
    } catch (err) {
      setFormError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("new");
    setParams(next);
  };

  return (
    <div data-testid="owner-businesses-page">
      <PageHeader overline="Business management" title="Businesses"
        subtitle="Create hospitals, pharmacies or combined businesses, invite their administrator and open a full business dashboard."
        actions={
          <button onClick={() => setShowForm((s) => !s)} data-testid="toggle-create-business-btn"
            className="rounded-full bg-[#0F172A] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white">
            {showForm ? "Close form" : "Add business"}
          </button>
        } />

      {showForm && (
        <form onSubmit={submit} className="panel p-6 md:p-8 mb-8 fade-up" data-testid="create-business-form">          <h3 className="font-ui text-lg font-bold tracking-tight">New business</h3>
          <p className="text-sm text-slate-500 mt-2">
            Creation is atomic — if the administrator invitation fails, no partial records are kept.
          </p>
          <div className="mt-7 grid gap-5 md:grid-cols-3">
            <label><span className={label}>Business name *</span>
              <input data-testid="biz-name-input" className={`mt-2 ${field}`} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Err msg={errors.name} /></label>
            <label><span className={label}>Business type *</span>
              <select data-testid="biz-type-select" className={`mt-2 ${field}`} value={form.business_type}
                onChange={(e) => setForm({ ...form, business_type: e.target.value })}>
                <option value="hospital">Hospital</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="lab">Independent Lab</option>
                <option value="hospital_pharmacy">Hospital + Pharmacy</option>
                <option value="hospital_lab">Hospital + Laboratory</option>
                <option value="hospital_pharmacy_lab">Hospital + Laboratory + Pharmacy</option>
              </select></label>
            <label><span className={label}>City *</span>
              <input data-testid="biz-city-input" className={`mt-2 ${field}`} value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <Err msg={errors.city} /></label>
            <label><span className={label}>State *</span>
              <select data-testid="biz-state-select" className={`mt-2 ${field}`} value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}>
                <option value="">Select state</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Err msg={errors.state} /></label>
            <label><span className={label}>PIN code</span>
              <input data-testid="biz-pincode-input" className={`mt-2 ${field}`} value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
              <Err msg={errors.pincode} /></label>
            <div><PhoneInput label="Business phone" required value={form.phone} testid="biz-phone"
              error={errors.phone} onChange={(v) => setForm({ ...form, phone: v })} /></div>
            <label><span className={label}>Business email *</span>
              <input data-testid="biz-email-input" className={`mt-2 ${field}`} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Err msg={errors.email} /></label>
            <label className="md:col-span-2"><span className={label}>Address *</span>
              <input data-testid="biz-address-input" className={`mt-2 ${field}`} value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <Err msg={errors.address} /></label>
          </div>

          {form.business_type.includes("hospital") && (
            <div className="mt-8 border-t border-slate-200 pt-7" data-testid="hospital-details-section">
              <p className="overline text-[#EA580C]">Hospital details</p>
              <div className="mt-5 grid gap-5 md:grid-cols-3">
                <label><span className={label}>Hospital registration number *</span>
                  <input data-testid="biz-registration-input" className={`mt-2 ${field}`} value={form.registration_no}
                    onChange={(e) => setForm({ ...form, registration_no: e.target.value.toUpperCase() })} />
                  <Err msg={errors.registration_no} /></label>
              </div>
            </div>
          )}

          {form.business_type.includes("pharmacy") && (
            <div className="mt-8 border-t border-slate-200 pt-7" data-testid="pharmacy-details-section">
              <p className="overline text-[#EA580C]">Pharmacy details</p>
              <div className="mt-5 grid gap-5 md:grid-cols-3">
                <label><span className={label}>Drug licence number *</span>
                  <input data-testid="biz-drug-licence-input" className={`mt-2 ${field}`} value={form.drug_licence}
                    onChange={(e) => setForm({ ...form, drug_licence: e.target.value.toUpperCase() })} />
                  <Err msg={errors.drug_licence} /></label>
                {form.business_type === "hospital_pharmacy" && (
                  <label><span className={label}>Pharmacy trade name</span>
                    <input className={`mt-2 ${field}`} value={form.pharmacy_name}
                      onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })} /></label>
                )}
              </div>
            </div>
          )}

          <div className="mt-8 border-t border-slate-200 pt-7">
            <p className="overline text-[#EA580C]">GST / tax registration</p>
            <div className="mt-5 grid gap-5 md:grid-cols-3">
              <label><span className={label}>GSTIN {form.business_type.includes("pharmacy") ? "*" : "(optional)"}</span>
                <input data-testid="biz-gstin-input" className={`mt-2 ${field}`} value={form.gstin}
                  placeholder="22AAAAA0000A1Z5"
                  onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} />
                <Err msg={errors.gstin} /></label>
              <p className="md:col-span-2 self-center text-xs text-slate-500">
                Format and checksum are validated and the GSTIN must be unique across the platform.
                This is not a GST portal verification.
              </p>
            </div>
          </div>

          <div className="mt-8 border-t border-slate-200 pt-7">
            <p className="overline text-[#EA580C]">Business administrator</p>
            <div className="mt-5 grid gap-5 md:grid-cols-3">
              <label><span className={label}>Admin name *</span>
                <input data-testid="admin-name-input" className={`mt-2 ${field}`} value={form.admin_name}
                  onChange={(e) => setForm({ ...form, admin_name: e.target.value })} />
                <Err msg={errors.admin_name} /></label>
              <label><span className={label}>Admin email *</span>
                <input type="email" data-testid="admin-email-input" className={`mt-2 ${field}`} value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
                <Err msg={errors.admin_email} /></label>
              <div><PhoneInput label="Admin mobile" required value={form.admin_phone} testid="admin-phone"
                error={errors.admin_phone} onChange={(v) => setForm({ ...form, admin_phone: v })} /></div>
            </div>
          </div>
          {formError && <p className="mt-5 text-sm text-red-600" data-testid="create-business-error">{formError}</p>}
          <button type="submit" disabled={busy} data-testid="create-business-submit-btn"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-7 py-3 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create business & invite admin
          </button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input placeholder="Search business name" data-testid="business-search-input"
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none focus:border-slate-900 w-full sm:w-72"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={status}
          data-testid="business-status-filter" onChange={(e) => setFilter("status", e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={type}
          data-testid="business-type-filter" onChange={(e) => setFilter("type", e.target.value)}>
          <option value="">All types</option>
          <option value="hospital">Hospital</option>
          <option value="pharmacy">Pharmacy</option>
          <option value="lab">Independent Lab</option>
          <option value="hospital_pharmacy">Hospital + Pharmacy</option>
                <option value="hospital_lab">Hospital + Laboratory</option>
                <option value="hospital_pharmacy_lab">Hospital + Laboratory + Pharmacy</option>
        </select>
      </div>

      {error && <ErrorState message={error} />}
      {!items && !error && <Loading label="Loading businesses…" />}
      {items && items.length === 0 && (
        <div className="panel">
          <EmptyState title="No businesses yet"
            subtitle="Create your first hospital or pharmacy to begin. No demo data is seeded."
            testid="businesses-empty" />
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {items.map((b) => (
            <div key={b.business_id}
              data-testid={`business-card-${b.business_id}`}
              className="panel p-6">
              <Link to={`/owner/businesses/${b.business_id}`} data-testid={`business-open-${b.business_id}`} className="block hover:text-orange-600">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-ui text-lg font-bold tracking-tight">{b.name}</h3>
                  <p className="text-xs text-slate-400 mt-1 font-mono-num">{b.code}</p>
                </div>
                <StatusPill status={b.status} />
              </div>
              <p className="text-xs text-slate-500 mt-4">{BUSINESS_TYPE_LABEL[b.business_type]}</p>
              <p className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                <MapPin className="h-3.5 w-3.5" /> {[b.city, b.state].filter(Boolean).join(", ") || "Location not set"}
              </p>
              <div className="mt-5 grid grid-cols-3 gap-3 border-t border-slate-100 pt-5">
                <div><p className="overline text-slate-400">Staff</p><p className="font-ui font-bold mt-1">{num(b.stats.staff)}</p></div>
                <div><p className="overline text-slate-400">Patients</p><p className="font-ui font-bold mt-1">{num(b.stats.patients)}</p></div>
                <div><p className="overline text-slate-400">Revenue</p><p className="font-ui font-bold mt-1 text-[#EA580C]">{inrShort(b.stats.pharmacy_revenue + b.stats.hospital_revenue)}</p></div>
              </div>
              <div className="mt-5 flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{b.admin ? b.admin.name : "No admin"}</span>
                <span>Created {dmy(b.created_at)}</span>
              </div>
              </Link>
              <ModuleLinks business={b} onChange={load} />
              <Link to="/owner/groups" data-testid={`business-group-${b.business_id}`} className="mt-3 block text-xs text-orange-700">{b.group_id ? "Manage linked businesses" : "Link an existing business"}</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
